import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Edit2, Trash2, FileText, Download, Save, Plus, 
  Trash, RefreshCw, User, MapPin, Building, Camera, X, Upload,
  AlertTriangle, CheckCircle, AlertCircle, TrendingUp, Shield, Loader2, Eye
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import DevisForm from '@/components/forms/DevisForm';
import ConvertDevisToFactureDialog from '@/components/ConvertDevisToFactureDialog';
import RentabiliteWidget from '@/components/RentabiliteWidget';
import LastTouchedBadge from '@/components/LastTouchedBadge';
import DevisLineMaterials from '@/components/devis/DevisLineMaterials';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { parseFR } from '@/lib/utils';
import NumInputFR from '@/components/NumInputFR';
import PrevNextNav from '@/components/PrevNextNav';
import { usePrevNextNav } from '@/hooks/usePrevNextNav';

export default function DevisDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const nav = usePrevNextNav({
    storageKey: 'devis_list_order',
    currentId: id,
    routePrefix: '/devis',
  });
  const [devis, setDevis] = useState(null);
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEdit, setShowEdit] = useState(false);
  const [generating, setGenerating] = useState(false);
  
  // Editable state for AI-generated devis
  const [isEditing, setIsEditing] = useState(false);
  const [editedDevis, setEditedDevis] = useState(null);
  const [editedLines, setEditedLines] = useState([]);
  const [showConvert, setShowConvert] = useState(false);
  const [linkedChantierId, setLinkedChantierId] = useState(null);
  const [clientInfo, setClientInfo] = useState({
    nom: '', adresse: '', telephone: '', email: '', type: 'particulier'
  });
  const [chantierAdresse, setChantierAdresse] = useState('');
  const [sameAsClient, setSameAsClient] = useState(true);
  const [projectTitle, setProjectTitle] = useState('');
  const [reserves, setReserves] = useState('');
  // CEE
  const [ceeEnabled, setCeeEnabled] = useState(false);
  const [ceeObligeId, setCeeObligeId] = useState('');
  const [ceePrimeMontant, setCeePrimeMontant] = useState(0);
  const [ceeObliges, setCeeObliges] = useState([]);
  // MPR (ligne séparée du CEE dans le PDF)
  const [mprPrimeMontant, setMprPrimeMontant] = useState(0);
  const [mprCategorie, setMprCategorie] = useState('');
  // Aperçu PDF (modale inline, pas de téléchargement)
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  
  // Photos state
  const [photos, setPhotos] = useState([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const photoInputRef = useRef(null);
  
  // AI Analysis state
  const [analysis, setAnalysis] = useState(null);
  const [analyzingDevis, setAnalyzingDevis] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(true);
  
  // Client selection & Status state
  const [gestionClients, setGestionClients] = useState([]);
  const [showClientSelector, setShowClientSelector] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState('En attente');
  const [statusObservation, setStatusObservation] = useState('');
  const [linkingClient, setLinkingClient] = useState(false);

  useEffect(() => {
    fetchDevis();
    fetchGestionClients();
    fetchCeeObliges();
  }, [id]);

  const fetchCeeObliges = async () => {
    try {
      const r = await api.get('/cee-obliges');
      const items = r.data.items || [];
      setCeeObliges(items);
      if (items.length === 0) {
        console.warn('[CEE] Aucun obligé retourné par le backend');
      }
    } catch (e) {
      console.error('[CEE] fetchCeeObliges KO', e);
      setCeeObliges([]);
    }
  };
  
  // Auto-analyze when devis is loaded
  useEffect(() => {
    if (devis && devis.lignes && devis.lignes.length > 0) {
      fetchAnalysis();
    }
  }, [devis?.devis_id]);
  
  // Fetch gestion clients for selector
  const fetchGestionClients = async () => {
    try {
      const response = await api.get('/gestion-clients/all');
      setGestionClients(response.data.clients || []);
    } catch (error) {
      console.error('Error fetching gestion clients:', error);
    }
  };
  
  // Link devis to a gestion client
  const handleLinkClient = async (clientId) => {
    setLinkingClient(true);
    try {
      const response = await api.patch(`/devis/${id}/link-client`, {
        gestion_client_id: clientId
      });
      if (response.data.success) {
        setClientInfo(response.data.client_info);
        setDevis({ ...devis, gestion_client_id: clientId, client_info: response.data.client_info });
        toast.success('Client lié au devis');
        setShowClientSelector(false);
      }
    } catch (error) {
      toast.error('Erreur lors de la liaison');
    }
    setLinkingClient(false);
  };

  // Unlink the currently linked client
  const handleUnlinkClient = async () => {
    if (!window.confirm('Vraiment délier ce client du devis ? Les infos client resteront figées dans le devis, mais le lien vers la fiche Gestion Clients sera supprimé.')) return;
    setLinkingClient(true);
    try {
      const response = await api.patch(`/devis/${id}/link-client`, {
        gestion_client_id: null
      });
      if (response.data.success) {
        setDevis({ ...devis, gestion_client_id: null });
        toast.success('Client délié du devis');
        setShowClientSelector(false);
      }
    } catch (error) {
      toast.error('Erreur lors de la déliaison');
    }
    setLinkingClient(false);
  };
  
  // Update devis status
  const handleUpdateStatus = async () => {
    try {
      await api.patch(`/devis/${id}/status`, {
        statut: newStatus,
        statut_observation: statusObservation
      });
      setDevis({ ...devis, statut: newStatus, statut_observation: statusObservation });
      toast.success('Statut mis à jour');
      setShowStatusModal(false);
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const fetchDevis = async () => {
    try {
      const response = await api.get(`/devis/${id}`);
      const data = response.data;
      setDevis(data);
      document.title = `Devis ${data.devis_id} - Reno Resolve`;
      
      // Initialize editable fields from devis data
      if (data.lignes && data.lignes.length > 0) {
        setEditedLines(data.lignes.map((l, idx) => ({
          id: idx,
          description: l.description || '',
          quantite: l.quantite || 1,
          unite: l.unite || 'u',
          prix_unitaire: l.prix_unitaire || 0,
          tva_rate: l.tva_rate || 20,
          total_ht: l.total_ht || 0,
          materials: Array.isArray(l.materials) ? l.materials : [],
          sous_traitant: l.sous_traitant || null,
        })));
      } else {
        // Initialize with one empty line for manual devis
        setEditedLines([{
          id: 0,
          description: data.description || 'Prestation',
          quantite: 1,
          unite: 'u',
          prix_unitaire: data.montant_estime || 0,
          tva_rate: 20,
          total_ht: data.montant_estime || 0,
          materials: [],
          sous_traitant: null,
        }]);
      }
      
      if (data.client_info) {
        setClientInfo({
          nom: data.client_info.nom || '',
          adresse: data.client_info.adresse || '',
          telephone: data.client_info.telephone || '',
          email: data.client_info.email || '',
          type: data.client_info.type || 'particulier'
        });
      }
      
      setChantierAdresse(data.chantier_adresse || '');
      setSameAsClient(!data.chantier_adresse || data.chantier_adresse === data.client_info?.adresse);
      setProjectTitle(data.project_title || '');
      setReserves(data.reserves || '');
      setCeeEnabled(!!data.cee_enabled);
      setCeeObligeId(data.cee_oblige_id || '');
      setCeePrimeMontant(Number(data.cee_prime_montant || 0));
      setMprPrimeMontant(Number(data.mpr_prime_montant || 0));
      setMprCategorie(data.mpr_categorie || '');
      setPhotos(data.photos || []);

      // Find linked chantier (if any)
      try {
        const chAll = await api.get('/chantiers');
        const linked = (chAll.data || []).find(c => c.devis_id === data.devis_id);
        if (linked) setLinkedChantierId(linked.chantier_id);
      } catch (e) { /* silent */ }
      
      // Fetch contact info
      if (data.contact_id) {
        try {
          const contactRes = await api.get(`/contacts/${data.contact_id}`);
          setContact(contactRes.data);
        } catch (err) {
          console.error('Erreur chargement contact:', err);
        }
      }
      setLoading(false);
    } catch (error) {
      console.error('Erreur chargement devis:', error);
      toast.error('Devis non trouvé');
      setLoading(false);
      setTimeout(() => navigate('/devis'), 2000);
    }
  };

  // Photo upload functions
  const handlePhotoUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setUploadingPhotos(true);
    try {
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append('files', files[i]);
      }
      
      const response = await api.post(`/devis/${id}/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        setPhotos(response.data.photos);
        toast.success(`${response.data.added} photo(s) ajoutée(s)`);
      }
    } catch (error) {
      console.error('Erreur upload photos:', error);
      toast.error('Erreur lors de l\'upload des photos');
    } finally {
      setUploadingPhotos(false);
      if (photoInputRef.current) photoInputRef.current.value = '';
    }
  };
  
  const handleDeletePhoto = async (index) => {
    try {
      const response = await api.delete(`/devis/${id}/photos/${index}`);
      if (response.data.success) {
        setPhotos(response.data.photos);
        toast.success('Photo supprimée');
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };
  
  // AI Analysis functions
  const fetchAnalysis = async () => {
    try {
      const response = await api.get(`/devis/${id}/analysis`);
      if (response.data.success && response.data.analysis) {
        setAnalysis(response.data.analysis);
      }
    } catch (error) {
      console.log('No cached analysis');
    }
  };
  
  const runAnalysis = async () => {
    setAnalyzingDevis(true);
    try {
      const response = await api.post(`/devis/${id}/analyze?force_refresh=true`);
      if (response.data.success) {
        setAnalysis(response.data.analysis);
        toast.success('Analyse IA terminée');
      }
    } catch (error) {
      console.error('Erreur analyse:', error);
      toast.error('Erreur lors de l\'analyse');
    } finally {
      setAnalyzingDevis(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Voulez-vous vraiment supprimer ce devis ?')) return;
    
    try {
      await api.delete(`/devis/${id}`);
      toast.success('Devis supprimé');
      navigate('/devis');
    } catch (error) {
      console.error('Erreur suppression devis:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDevisUpdated = () => {
    setShowEdit(false);
    fetchDevis();
    toast.success('Devis mis à jour');
  };

  // Line editing functions
  const updateLine = (index, field, value) => {
    setEditedLines(prev => prev.map((line, idx) => {
      if (idx === index) {
        const updated = { ...line, [field]: value };
        // Recalculate total_ht when quantity or price changes
        if (field === 'quantite' || field === 'prix_unitaire') {
          updated.total_ht = (updated.quantite || 0) * (updated.prix_unitaire || 0);
        }
        return updated;
      }
      return line;
    }));
  };

  const addLine = () => {
    setEditedLines(prev => [...prev, {
      id: Date.now(),
      description: 'Nouvelle ligne',
      quantite: 1,
      unite: 'u',
      prix_unitaire: 0,
      tva_rate: 20,
      total_ht: 0,
      materials: [],
      sous_traitant: null,
    }]);
  };

  // Met à jour le bloc matériaux/sous-traitant d'une ligne (interne, non imprimé).
  const updateLineMaterials = (index, next) => {
    setEditedLines(prev => prev.map((line, idx) =>
      idx === index
        ? { ...line, materials: next.materials || [], sous_traitant: next.sous_traitant || null }
        : line
    ));
  };

  const removeLine = (index) => {
    setEditedLines(prev => prev.filter((_, idx) => idx !== index));
  };

  // Calculate totals (revenue + internal cost + marges)
  const calculateTotals = () => {
    let totalHT = 0;
    let totalTVA = 0;
    let coutMateriaux = 0;
    let coutSousTraitants = 0;
    
    editedLines.forEach(line => {
      const lineHT = (line.quantite || 0) * (line.prix_unitaire || 0);
      const lineTVA = lineHT * ((line.tva_rate || 20) / 100);
      totalHT += lineHT;
      totalTVA += lineTVA;
      (line.materials || []).forEach(m => {
        coutMateriaux += (Number(m.prix_unitaire_ht) || 0) * (Number(m.quantite) || 0);
      });
      if (line.sous_traitant && line.sous_traitant.prix_ht) {
        coutSousTraitants += Number(line.sous_traitant.prix_ht) || 0;
      }
    });
    
    const ttc = totalHT + totalTVA;
    const coutInterne = coutMateriaux + coutSousTraitants;
    const resteACharge = ttc - (Number(ceePrimeMontant) || 0) - (Number(mprPrimeMontant) || 0);
    // Marge brute = HT − coût interne (avant aides)
    const margeBrute = totalHT - coutInterne;
    const margeBrutePct = totalHT > 0 ? (margeBrute / totalHT) * 100 : 0;
    // Marge nette = Reste à charge − coût interne (client paie ça après aides)
    const margeNette = resteACharge - coutInterne;
    const margeNettePct = resteACharge > 0 ? (margeNette / resteACharge) * 100 : 0;
    
    return {
      ht: totalHT, tva: totalTVA, ttc,
      coutMateriaux, coutSousTraitants, coutInterne,
      resteACharge,
      margeBrute, margeBrutePct,
      margeNette, margeNettePct,
    };
  };

  // Détecte les lignes SANS matériaux (bloque la sauvegarde — règle P0)
  const linesWithoutMaterials = () =>
    editedLines
      .map((l, i) => ({ i, l }))
      .filter(({ l }) => !Array.isArray(l.materials) || l.materials.length === 0);

  // Save changes — BLOQUANT si une ligne n'a pas de matériau (règle P0)
  const handleSaveChanges = async () => {
    const missing = linesWithoutMaterials();
    if (missing.length > 0) {
      toast.error(
        `Impossible d'enregistrer : ${missing.length} ligne(s) sans matériau. ` +
        `Ajoute au moins un matériau par ligne dans l'onglet "🔧 Matériaux".`
      );
      return;
    }
    const totals = calculateTotals();
    
    try {
      // 1) Sauvegarde du devis (avec matériaux + sous-traitants embarqués sur lignes)
      await api.patch(`/devis/${id}`, {
        project_title: projectTitle,
        reserves: reserves,
        cee_enabled: ceeEnabled,
        cee_oblige_id: ceeObligeId || null,
        cee_prime_montant: Number(ceePrimeMontant) || 0,
        mpr_prime_montant: Number(mprPrimeMontant) || 0,
        mpr_categorie: mprCategorie || '',
        client_info: clientInfo,
        chantier_adresse: sameAsClient ? clientInfo.adresse : chantierAdresse,
        lignes: editedLines.map(l => ({
          description: l.description,
          quantite: l.quantite,
          unite: l.unite,
          prix_unitaire: l.prix_unitaire,
          tva_rate: l.tva_rate,
          total_ht: l.quantite * l.prix_unitaire,
          materials: l.materials || [],
          sous_traitant: l.sous_traitant || null,
        })),
        totaux: { ht: totals.ht, tva: totals.tva, ttc: totals.ttc },
        montant_estime: totals.ht,
        montant_total: totals.ttc,
      });

      // 2) Sauvegarde auto de chaque matériau dans le catalogue Fournisseurs
      //    (idempotent côté backend : dédoublonnage sur (nom_normalisé, fournisseur))
      const uniqueMaterials = [];
      const seen = new Set();
      editedLines.forEach(l => {
        (l.materials || []).forEach(m => {
          if (!m.nom || !m.nom.trim()) return;
          const k = `${m.nom.trim().toLowerCase()}||${(m.fournisseur || '').trim().toLowerCase()}`;
          if (seen.has(k)) return;
          seen.add(k);
          uniqueMaterials.push(m);
        });
      });
      if (uniqueMaterials.length > 0) {
        try {
          await Promise.all(uniqueMaterials.map(m =>
            api.post('/fournisseurs/catalogue', {
              nom: m.nom,
              reference: m.reference || '',
              fournisseur: m.fournisseur || '',
              prix_unitaire: Number(m.prix_unitaire_ht) || 0,
              unite: m.unite || 'u',
              categorie: m.categorie || '',
              description: '',
              lien_url: m.lien_url || '',
            }).catch(e => console.warn('Save mat KO', m.nom, e))
          ));
        } catch (e) {
          console.warn('Batch save materials KO', e);
        }
      }
      
      toast.success(
        uniqueMaterials.length > 0
          ? `Devis mis à jour · ${uniqueMaterials.length} matériau(x) référencé(s) dans le catalogue`
          : 'Devis mis à jour'
      );
      setIsEditing(false);
      fetchDevis();
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  // Aperçu PDF — ouvre directement dans un nouvel onglet.
  // Correctif : Chrome/Safari bloquent `window.open` appelé APRÈS un `await`
  // (chaîne de user-gesture cassée). On ouvre un onglet blank SYNC dès le clic,
  // puis on y injecte l'URL blob une fois le PDF récupéré.
  const handlePreviewPdf = async () => {
    // 1) Ouverture SYNC — préserve la user-gesture (contourne le popup blocker)
    const w = window.open('', '_blank', 'noopener,noreferrer');
    try {
      const response = await api.get(`/devis/${id}/preview`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      if (w && !w.closed) {
        // 2) Injecte l'URL blob dans l'onglet déjà ouvert
        w.location.href = url;
      } else {
        // 3) Popup bloqué malgré le sync-open → fallback téléchargement direct
        //    (aucun `window.open` supplémentaire → jamais bloqué)
        const a = document.createElement('a');
        a.href = url;
        a.download = `Devis_${devis?.devis_id || id}.pdf`;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        setTimeout(() => { try { document.body.removeChild(a); } catch (_) {} }, 1500);
        toast.info("Onglet bloqué par le navigateur — PDF téléchargé à la place.");
      }
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      console.error('Erreur aperçu:', e);
      try { if (w && !w.closed) w.close(); } catch (_) {}
      // Le response.data est un Blob (à cause de responseType='blob') —
      // on tente de le décoder en JSON pour extraire le vrai `detail`.
      let detail = e?.message;
      const status = e?.response?.status;
      const rawData = e?.response?.data;
      if (rawData && typeof rawData.text === 'function') {
        try {
          const txt = await rawData.text();
          try { detail = JSON.parse(txt)?.detail || txt || detail; }
          catch { detail = txt || detail; }
        } catch (_) { /* ignore */ }
      } else if (rawData?.detail) {
        detail = rawData.detail;
      }
      toast.error(
        status
          ? `Aperçu PDF impossible (HTTP ${status})${detail ? ' — ' + detail : ''}`
          : "Impossible d'afficher l'aperçu du PDF",
      );
    }
  };

  // Ouvre dans un nouvel onglet (alias explicite du bouton d'aperçu)
  const handleOpenPdfInTab = handlePreviewPdf;

  // Téléchargement direct via l'endpoint preview (déjà stable).
  const handleDownloadPdf = async () => {
    try {
      const response = await api.get(`/devis/${id}/preview`, { responseType: 'blob' });
      // Détecte un blob qui contient en fait du JSON d'erreur (axios laisse
      // passer les codes 2xx uniquement, mais on couvre le cas mixte)
      const blob = new Blob([response.data], { type: 'application/pdf' });
      if (blob.size < 100) {
        throw new Error(`PDF vide reçu (${blob.size} octets)`);
      }
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Devis_${devis?.devis_id || id}.pdf`;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        try { document.body.removeChild(a); } catch (_) {}
        window.URL.revokeObjectURL(url);
      }, 1500);
      toast.success('Téléchargement lancé');
    } catch (e) {
      console.error('Download error:', e);
      let detail = e?.message;
      const status = e?.response?.status;
      const rawData = e?.response?.data;
      if (rawData && typeof rawData.text === 'function') {
        try {
          const txt = await rawData.text();
          try { detail = JSON.parse(txt)?.detail || txt || detail; }
          catch { detail = txt || detail; }
        } catch (_) { /* ignore */ }
      } else if (rawData?.detail) {
        detail = rawData.detail;
      }
      toast.error(
        status
          ? `Téléchargement impossible (HTTP ${status})${detail ? ' — ' + detail : ''}`
          : 'Erreur lors du téléchargement',
      );
    }
  };

  // Regenerate PDF
  const handleRegeneratePDF = async () => {
    setGenerating(true);
    const totals = calculateTotals();
    
    try {
      const pdfData = {
        file_id: devis.source_file_id || 'manual',
        client: clientInfo,
        devis_info: {
          numero: devis.devis_id,
          date: format(new Date(), 'dd/MM/yyyy'),
          validite: '30 jours',
          reserves: reserves,
          cee_enabled: ceeEnabled,
          cee_oblige_id: ceeObligeId || null,
          cee_prime_montant: Number(ceePrimeMontant) || 0,
          mpr_prime_montant: Number(mprPrimeMontant) || 0,
          mpr_categorie: mprCategorie || '',
        },
        project_title: projectTitle,
        chantier_adresse: sameAsClient ? clientInfo.adresse : chantierAdresse,
        before_photo_urls: photos || [],  // Use uploaded photos
        lignes: editedLines.map(l => ({
          description: l.description,
          quantite: l.quantite,
          unite: l.unite,
          prix_unitaire: l.prix_unitaire,
          tva_rate: l.tva_rate,
          total_ht: l.quantite * l.prix_unitaire
        })),
        totaux: totals,
        discount_mode: 'none',
        discount_value: 0
      };
      
      const response = await api.post('/devis-copier/generate-pdf-v2', pdfData, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Devis_${devis.devis_id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('PDF généré et téléchargé !');
    } catch (error) {
      console.error('Erreur génération PDF:', error);
      toast.error('Erreur lors de la génération du PDF');
    } finally {
      setGenerating(false);
    }
  };

  const getStatutBadgeVariant = (statut) => {
    const variants = {
      Brouillon: 'secondary',
      'Envoyé': 'default',
      'Relancé': 'secondary',
      'Accepté': 'default',
      'Refusé': 'destructive',
    };
    return variants[statut] || 'default';
  };

  const getSourceBadge = (source) => {
    if (source === 'copie_concurrent') {
      return <Badge variant="outline" className="text-purple-600 border-purple-200 bg-purple-50">Copie IA</Badge>;
    }
    if (source === 'generateur_ia') {
      return <Badge variant="outline" className="text-blue-600 border-blue-200 bg-blue-50">Généré IA</Badge>;
    }
    return <Badge variant="outline">Manuel</Badge>;
  };

  const totals = calculateTotals();
  // All devis can be edited with lines now
  const isAIDevis = true; // Treat all devis as editable with lines

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/devis')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <PrevNextNav {...nav} />
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Devis {devis.devis_id}
              </h1>
              {getSourceBadge(devis.source)}
              {/* Status badge - clickable to change */}
              <button 
                onClick={() => {
                  setNewStatus(devis.statut || 'En attente');
                  setStatusObservation(devis.statut_observation || '');
                  setShowStatusModal(true);
                }}
                className={`px-3 py-1 rounded-full text-sm font-medium cursor-pointer hover:opacity-80 transition-opacity ${
                  devis.statut === 'Accepté' ? 'bg-green-100 text-green-700' :
                  devis.statut === 'Refusé' ? 'bg-red-100 text-red-700' :
                  'bg-amber-100 text-amber-700'
                }`}
                data-testid="status-badge"
              >
                {devis.statut || 'En attente'} ▼
              </button>
            </div>
            {projectTitle && (
              <p className="text-slate-600 mt-1">{projectTitle}</p>
            )}
            {/* Show status observation if exists */}
            {devis.statut_observation && (
              <p className="text-sm text-slate-500 italic mt-1">"{devis.statut_observation}"</p>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {/* Link to client button */}
          <Button 
            variant="outline" 
            onClick={() => setShowClientSelector(true)}
            className="border-blue-300 text-blue-700 hover:bg-blue-50"
          >
            <User className="w-4 h-4 mr-2" />
            {devis.gestion_client_id ? 'Changer client' : 'Lier à un client'}
          </Button>
          {isAIDevis && !isEditing && (
            <Button variant="outline" onClick={() => setIsEditing(true)}>
              <Edit2 className="w-4 h-4 mr-2" />
              Modifier
            </Button>
          )}
          {isAIDevis && isEditing && (
            <>
              <Button variant="outline" onClick={() => setIsEditing(false)}>
                Annuler
              </Button>
              <Button onClick={handleSaveChanges} className="bg-green-600 hover:bg-green-700">
                <Save className="w-4 h-4 mr-2" />
                Sauvegarder
              </Button>
            </>
          )}
          {!isAIDevis && (
            <Button variant="outline" onClick={() => setShowEdit(true)}>
              <Edit2 className="w-4 h-4 mr-2" />
              Modifier
            </Button>
          )}
          <Button 
            variant="outline"
            onClick={handlePreviewPdf}
            className="border-slate-300 text-slate-700 hover:bg-slate-100"
            data-testid="devis-preview-btn"
          >
            <Eye className="w-4 h-4 mr-2" />
            Aperçu
          </Button>
          <Button 
            onClick={handleDownloadPdf} 
            disabled={generating}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="devis-download-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Télécharger PDF
          </Button>
          {/* Voir le dossier CEE — visible si ce devis a été généré depuis un dossier CEE */}
          {devis.cee_dossier_id && (
            <Button
              key="open-cee-dossier"
              variant="outline"
              onClick={() => navigate(`/cee/dossiers/${devis.cee_dossier_id}`)}
              className="border-red-300 text-red-700 hover:bg-red-50"
              data-testid="open-cee-dossier-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              Voir le dossier CEE
            </Button>
          )}
          {/* Voir le chantier — visible dès qu'un chantier existe pour ce devis */}
          {linkedChantierId && (
            <Button
              key="open-chantier"
              variant="outline"
              onClick={() => navigate(`/chantiers/${linkedChantierId}`)}
              className="border-blue-300 text-blue-700 hover:bg-blue-50"
              data-testid="open-chantier-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              Voir le chantier
            </Button>
          )}
          {/* Convert to invoice — only when the devis is signed and not yet converted */}
          {devis.facture_id ? (
            <Button
              key="open-facture"
              variant="outline"
              onClick={() => navigate(`/factures/${devis.facture_id}`)}
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              data-testid="open-facture-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              Voir la facture
            </Button>
          ) : (
            <Button
              key="convert-facture"
              onClick={() => setShowConvert(true)}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="convert-to-facture-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              Convertir en facture
            </Button>
          )}
          <Button variant="destructive" onClick={handleDelete}>
            <Trash2 className="w-4 h-4 mr-2" />
            Supprimer
          </Button>
        </div>
      </div>

      {/* Main Content */}
      {isAIDevis ? (
        /* Organisation en onglets pour ne pas bombarder d'infos.
           Onglet 1 "Devis" = édition primaire (lignes, aides, totaux).
           Onglet 2 "Analyse & Chantier" = rentabilité, IA, photos, notes. */
        <Tabs defaultValue="devis" className="w-full" data-testid="devis-tabs">
          <TabsList className="grid w-full sm:w-auto sm:inline-grid grid-cols-2 mb-4">
            <TabsTrigger value="devis" data-testid="tab-devis" className="text-sm">
              <FileText className="w-4 h-4 mr-2" />Devis & Aides
            </TabsTrigger>
            <TabsTrigger value="suivi" data-testid="tab-suivi" className="text-sm">
              <TrendingUp className="w-4 h-4 mr-2" />Analyse & Chantier
            </TabsTrigger>
          </TabsList>

          <TabsContent value="devis" className="space-y-6 mt-0">
          {/* Client & Chantier Info */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Client Info */}
            <div className="bg-white rounded-lg p-6 shadow-sm border border-slate-200">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <User className="w-4 h-4 text-blue-600" />
                Informations Client
              </h3>
              
              {isEditing ? (
                <div className="space-y-4">
                  <div>
                    <Label>Type de client</Label>
                    <RadioGroup 
                      value={clientInfo.type} 
                      onValueChange={(v) => setClientInfo(prev => ({ ...prev, type: v }))}
                      className="flex gap-4 mt-1"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="particulier" id="part" />
                        <Label htmlFor="part">Particulier</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="professionnel" id="pro" />
                        <Label htmlFor="pro">Professionnel</Label>
                      </div>
                    </RadioGroup>
                  </div>
                  <div>
                    <Label>Nom</Label>
                    <Input 
                      value={clientInfo.nom} 
                      onChange={(e) => setClientInfo(prev => ({ ...prev, nom: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Adresse</Label>
                    <Input 
                      value={clientInfo.adresse} 
                      onChange={(e) => setClientInfo(prev => ({ ...prev, adresse: e.target.value }))}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label>Téléphone</Label>
                      <Input 
                        value={clientInfo.telephone} 
                        onChange={(e) => setClientInfo(prev => ({ ...prev, telephone: e.target.value }))}
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input 
                        value={clientInfo.email} 
                        onChange={(e) => setClientInfo(prev => ({ ...prev, email: e.target.value }))}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="font-medium text-slate-900">{clientInfo.nom || '-'}</p>
                  <p className="text-sm text-slate-600">{clientInfo.adresse || '-'}</p>
                  <p className="text-sm text-slate-600">Tél: {clientInfo.telephone || '-'}</p>
                  <p className="text-sm text-slate-600">Email: {clientInfo.email || '-'}</p>
                  <Badge variant="outline" className="mt-2">
                    {clientInfo.type === 'professionnel' ? 'Professionnel' : 'Particulier'}
                  </Badge>
                </div>
              )}
            </div>

            {/* Chantier Info */}
            <div className="bg-white rounded-lg p-6 shadow-sm border border-slate-200">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-blue-600" />
                Adresse du Chantier
              </h3>
              
              {isEditing ? (
                <div className="space-y-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={sameAsClient}
                      onChange={(e) => setSameAsClient(e.target.checked)}
                      className="rounded border-slate-300"
                    />
                    <span className="text-sm">Même adresse que le client</span>
                  </label>
                  {!sameAsClient && (
                    <Input 
                      value={chantierAdresse} 
                      onChange={(e) => setChantierAdresse(e.target.value)}
                      placeholder="Adresse du chantier"
                    />
                  )}
                  <div>
                    <Label>Objet du devis</Label>
                    <Input 
                      value={projectTitle} 
                      onChange={(e) => setProjectTitle(e.target.value)}
                      placeholder="Ex: Rénovation énergétique..."
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-slate-900">
                    {sameAsClient ? clientInfo.adresse : chantierAdresse || "Non spécifié"}
                  </p>
                  {projectTitle && (
                    <p className="text-sm text-slate-600 mt-3">
                      <strong>Objet:</strong> {projectTitle}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Lines Table */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
            <div className="p-4 bg-slate-50 border-b border-slate-200 flex justify-between items-center">
              <h3 className="font-semibold text-slate-900">
                Lignes du devis ({editedLines.length})
              </h3>
              {isEditing && (
                <Button size="sm" variant="outline" onClick={addLine}>
                  <Plus className="w-4 h-4 mr-1" />
                  Ajouter
                </Button>
              )}
            </div>
            
            <div className="overflow-x-auto">
              <div className="w-full" role="table">
                <div role="row" className={`grid ${isEditing ? 'grid-cols-[1fr_80px_80px_100px_80px_120px_50px]' : 'grid-cols-[1fr_80px_80px_100px_80px_120px]'} bg-slate-100`}>
                  <div role="columnheader" className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Description</div>
                  <div role="columnheader" className="text-center px-2 py-3 text-xs font-semibold text-slate-600 uppercase">Qté</div>
                  <div role="columnheader" className="text-center px-2 py-3 text-xs font-semibold text-slate-600 uppercase">Unité</div>
                  <div role="columnheader" className="text-center px-2 py-3 text-xs font-semibold text-slate-600 uppercase">P.U. HT</div>
                  <div role="columnheader" className="text-center px-2 py-3 text-xs font-semibold text-slate-600 uppercase">TVA %</div>
                  <div role="columnheader" className="text-right px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Total HT</div>
                  {isEditing && <div role="columnheader"></div>}
                </div>
                <div role="rowgroup" className="divide-y divide-slate-100">
                  {editedLines.map((line, idx) => (
                    <div key={line.id} className="flex flex-col">
                      <div role="row" className={`grid ${isEditing ? 'grid-cols-[1fr_80px_80px_100px_80px_120px_50px]' : 'grid-cols-[1fr_80px_80px_100px_80px_120px]'} items-center hover:bg-slate-50`}>
                      <div role="cell" className="px-4 py-3">
                        {isEditing ? (
                          <Textarea
                            value={line.description}
                            onChange={(e) => updateLine(idx, 'description', e.target.value)}
                            className="min-h-[80px] resize-y whitespace-pre-line leading-relaxed"
                            rows={4}
                          />
                        ) : (
                          <span className="text-slate-900 whitespace-pre-line block leading-relaxed">{line.description}</span>
                        )}
                      </div>
                      <div role="cell" className="px-2 py-3 text-center">
                        {isEditing ? (
                          <NumInputFR
                            value={line.quantite}
                            onChange={(n) => updateLine(idx, 'quantite', n)}
                            className="w-20 text-center"
                          />
                        ) : (
                          <span>{line.quantite}</span>
                        )}
                      </div>
                      <div role="cell" className="px-2 py-3 text-center">
                        {isEditing ? (
                          <Select value={line.unite} onValueChange={(v) => updateLine(idx, 'unite', v)}>
                            <SelectTrigger className="w-20">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="u">u</SelectItem>
                              <SelectItem value="m²">m²</SelectItem>
                              <SelectItem value="ml">ml</SelectItem>
                              <SelectItem value="m³">m³</SelectItem>
                              <SelectItem value="h">h</SelectItem>
                              <SelectItem value="forfait">forfait</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <span>{line.unite}</span>
                        )}
                      </div>
                      <div role="cell" className="px-2 py-3 text-center">
                        {isEditing ? (
                          <NumInputFR
                            value={line.prix_unitaire}
                            onChange={(n) => updateLine(idx, 'prix_unitaire', n)}
                            className="w-24 text-right"
                          />
                        ) : (
                          <span>{line.prix_unitaire?.toFixed(2)} €</span>
                        )}
                      </div>
                      <div role="cell" className="px-2 py-3 text-center">
                        {isEditing ? (
                          <Select value={String(line.tva_rate)} onValueChange={(v) => updateLine(idx, 'tva_rate', parseFloat(v))}>
                            <SelectTrigger className="w-20">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="0">0%</SelectItem>
                              <SelectItem value="5.5">5.5%</SelectItem>
                              <SelectItem value="10">10%</SelectItem>
                              <SelectItem value="20">20%</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <span>{line.tva_rate}%</span>
                        )}
                      </div>
                      <div role="cell" className="px-4 py-3 text-right font-medium">
                        {((line.quantite || 0) * (line.prix_unitaire || 0)).toFixed(2)} €
                      </div>
                      {isEditing && (
                        <div role="cell" className="px-2 py-3 text-center">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => removeLine(idx)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                      </div>
                      {/* Panneau matériaux + sous-traitant (interne, jamais imprimé) */}
                      <div className="px-4 pb-3 -mt-1">
                        <DevisLineMaterials
                          value={{
                            materials: line.materials || [],
                            sous_traitant: line.sous_traitant || null,
                          }}
                          onChange={(next) => updateLineMaterials(idx, next)}
                          readOnly={!isEditing}
                          lineIndex={idx}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Bandeau coût interne — jamais imprimé sur le PDF */}
            <div className="p-4 border-t-4 border-slate-800 bg-gradient-to-r from-slate-50 to-slate-100" data-testid="devis-cout-interne">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-700 bg-slate-200 px-2 py-0.5 rounded">
                  🔒 Suivi interne
                </div>
                <span className="text-[11px] text-slate-500">
                  (visible seulement dans l'application — jamais imprimé sur le devis client)
                </span>
              </div>
              <div className="grid sm:grid-cols-5 gap-3 text-sm">
                <div>
                  <div className="text-[11px] uppercase text-slate-500 font-semibold">Coût matériaux</div>
                  <div className="text-lg font-bold text-slate-900" data-testid="cout-materiaux">
                    {totals.coutMateriaux.toFixed(2)} €
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase text-slate-500 font-semibold">Coût sous-traitants</div>
                  <div className="text-lg font-bold text-slate-900" data-testid="cout-sous-traitants">
                    {totals.coutSousTraitants.toFixed(2)} €
                  </div>
                </div>
                <div className="border-l border-slate-300 pl-3">
                  <div className="text-[11px] uppercase text-slate-500 font-semibold">Coût interne total</div>
                  <div className="text-lg font-bold text-blue-700" data-testid="cout-interne">
                    {totals.coutInterne.toFixed(2)} €
                  </div>
                </div>
                <div className="border-l border-slate-300 pl-3">
                  <div className="text-[11px] uppercase text-slate-500 font-semibold">Marge brute (HT − coûts)</div>
                  <div className={`text-lg font-bold ${totals.margeBrute >= 0 ? 'text-emerald-700' : 'text-red-600'}`}
                       data-testid="marge-brute">
                    {totals.margeBrute.toFixed(2)} €
                    <span className="text-xs ml-1 font-medium">
                      ({totals.margeBrutePct.toFixed(1)} %)
                    </span>
                  </div>
                </div>
                <div className="border-l border-slate-300 pl-3">
                  <div className="text-[11px] uppercase text-slate-500 font-semibold">Marge nette (Reste à charge − coûts)</div>
                  <div className={`text-lg font-bold ${totals.margeNette >= 0 ? 'text-emerald-700' : 'text-red-600'}`}
                       data-testid="marge-nette">
                    {totals.margeNette.toFixed(2)} €
                    <span className="text-xs ml-1 font-medium">
                      ({totals.margeNettePct.toFixed(1)} %)
                    </span>
                  </div>
                </div>
              </div>
              {(() => {
                const missing = linesWithoutMaterials();
                if (missing.length === 0) return null;
                return (
                  <div className="mt-3 text-xs font-semibold text-amber-900 bg-amber-100 border border-amber-300 rounded px-3 py-1.5"
                       data-testid="devis-blocking-warn">
                    ⚠️ {missing.length} ligne(s) sans matériau — la sauvegarde est bloquée tant que chaque ligne n'a pas au moins un matériau associé.
                  </div>
                );
              })()}
            </div>

            {/* Totals — vue client (HT / TVA / TTC + CEE + MPR + Reste à charge) */}
            <div className="p-4 bg-slate-50 border-t border-slate-200">
              <div className="flex justify-end">
                <div className="w-72 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Total HT</span>
                    <span className="font-medium">{totals.ht.toFixed(2)} €</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">TVA</span>
                    <span className="font-medium">{totals.tva.toFixed(2)} €</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t border-slate-300 pt-2">
                    <span>Total TTC</span>
                    <span className="text-blue-600">{totals.ttc.toFixed(2)} €</span>
                  </div>
                  {Number(ceePrimeMontant) > 0 && (
                    <div className="flex justify-between text-sm text-red-600 font-semibold" data-testid="total-cee">
                      <span>Prime CEE</span>
                      <span>−{Number(ceePrimeMontant).toFixed(2)} €</span>
                    </div>
                  )}
                  {Number(mprPrimeMontant) > 0 && (
                    <div className="flex justify-between text-sm text-purple-700 font-semibold" data-testid="total-mpr">
                      <span>MaPrimeRénov'{mprCategorie ? ` — cat. ${mprCategorie}` : ''}</span>
                      <span>−{Number(mprPrimeMontant).toFixed(2)} €</span>
                    </div>
                  )}
                  {(Number(ceePrimeMontant) > 0 || Number(mprPrimeMontant) > 0) && (
                    <div className="flex justify-between text-lg font-bold border-t-2 border-blue-600 pt-2" data-testid="total-reste-a-charge">
                      <span>Reste à charge</span>
                      <span className="text-blue-700">{totals.resteACharge.toFixed(2)} €</span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Réserves / observations */}
            <div className="p-4 border-t border-slate-200 bg-white">
              <label className="block text-sm font-semibold text-slate-800 mb-1.5">
                Réserves / observations
                <span className="ml-2 text-[11px] font-normal text-slate-500">
                  (facultatif — s'affiche sur le PDF sous les lignes du devis)
                </span>
              </label>
              <Textarea
                value={reserves}
                onChange={e => setReserves(e.target.value)}
                placeholder="ex : Sous réserve d'accord du syndic. Hors dépose amiante. Prix susceptibles d'être révisés en cas de découverte non visible en phase diagnostic..."
                rows={3}
                disabled={!isEditing}
                className="text-sm"
                data-testid="devis-reserves"
              />
            </div>

            {/* CEE — Prime + obligé */}
            <div className="p-4 border-t border-slate-200 bg-white" data-testid="devis-cee-block">
              <label className={`flex items-center gap-2 select-none ${isEditing ? 'cursor-pointer' : 'cursor-not-allowed opacity-70'}`}
                     onClick={(e) => {
                       if (!isEditing) { e.preventDefault(); toast.info('Clique sur "Modifier" en haut pour activer les modifications'); }
                     }}>
                <input
                  type="checkbox"
                  checked={ceeEnabled}
                  onChange={e => setCeeEnabled(e.target.checked)}
                  disabled={!isEditing}
                  className="w-4 h-4 rounded border-slate-400 accent-red-600 cursor-pointer"
                  data-testid="devis-cee-toggle"
                />
                <span className="text-sm font-semibold text-slate-800">
                  Devis lié à une prime CEE
                </span>
                <span className="ml-2 text-[11px] font-normal text-slate-500">
                  (Certificats d'Économie d'Énergie)
                </span>
                {!isEditing && (
                  <span className="ml-auto text-[11px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">
                    🔒 Passe en mode "Modifier" pour activer
                  </span>
                )}
              </label>

              {ceeEnabled && (
                <div className="mt-3 space-y-3 pl-6 border-l-2 border-red-200">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">
                        Obligé CEE *
                      </label>
                      <Select
                        value={ceeObligeId || undefined}
                        onValueChange={(v) => setCeeObligeId(v)}
                        disabled={!isEditing || ceeObliges.length === 0}
                      >
                        <SelectTrigger
                          className="w-full focus:border-red-500"
                          data-testid="devis-cee-oblige"
                        >
                          <SelectValue placeholder="— Sélectionner un obligé —" />
                        </SelectTrigger>
                        <SelectContent>
                          {ceeObliges.map(o => (
                            <SelectItem key={o.oblige_id} value={o.oblige_id}
                                        data-testid={`devis-cee-oblige-${o.oblige_id}`}>
                              {o.nom}{o.siren ? ` — SIREN ${o.siren}` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {ceeObliges.length === 0 && (
                        <p className="text-[11px] text-amber-700 mt-1">
                          ⚠️ Aucun obligé chargé.{' '}
                          <button
                            type="button"
                            onClick={fetchCeeObliges}
                            className="underline hover:text-amber-900 font-semibold"
                          >
                            Réessayer
                          </button>
                          {' '}ou vérifie{' '}
                          <a href="/parametres" className="underline hover:text-amber-900 font-semibold">Paramètres → Obligés CEE</a>.
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">
                        Montant prime CEE (€) *
                      </label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={ceePrimeMontant || ''}
                        onChange={e => setCeePrimeMontant(e.target.value)}
                        disabled={!isEditing}
                        placeholder="ex : 1500"
                        className="text-sm"
                        data-testid="devis-cee-prime"
                      />
                    </div>
                  </div>

                  {ceeObligeId && (() => {
                    const oblige = ceeObliges.find(o => o.oblige_id === ceeObligeId);
                    if (!oblige) return null;
                    return (
                      <div className="rounded-lg border-2 border-red-300 bg-red-50 p-3">
                        <div className="text-[11px] font-bold uppercase tracking-wider text-red-700 mb-1">
                          Mention RAI (Rôle Actif et Incitatif) — {oblige.nom}
                        </div>
                        <p className="text-xs text-red-900 leading-relaxed whitespace-pre-wrap">
                          {oblige.mention_rai || <em className="italic">Aucune mention RAI configurée pour cet obligé — ajoute-la depuis Paramètres → CEE.</em>}
                        </p>
                      </div>
                    );
                  })()}

                  <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600">
                    Sur le PDF, la prime CEE apparaîtra <span className="text-red-600 font-bold">en rouge</span> sous le Total TTC :
                    <span className="ml-1 font-semibold">Prime CEE — {ceeObliges.find(o => o.oblige_id === ceeObligeId)?.nom || '(obligé)'}</span>
                    {ceePrimeMontant > 0 && <> · <span className="text-red-600 font-bold">-{Number(ceePrimeMontant).toFixed(2)} €</span></>}
                    · <span className="font-semibold">Reste à charge : {(Number(totals.ttc) - Number(ceePrimeMontant || 0) - Number(mprPrimeMontant || 0)).toFixed(2)} €</span>
                  </div>
                </div>
              )}

              {/* MaPrimeRénov' — indépendant du CEE, ligne séparée dans le PDF */}
              <div className="mt-4 pt-4 border-t border-dashed border-slate-300" data-testid="devis-mpr-block">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm font-semibold text-purple-800">
                    🏛️ MaPrimeRénov' (optionnel — s'affiche en ligne distincte du CEE)
                  </span>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">
                      Montant MPR (€)
                    </label>
                    <Input
                      type="number" min="0" step="0.01"
                      value={mprPrimeMontant || ''}
                      onChange={e => setMprPrimeMontant(e.target.value)}
                      disabled={!isEditing}
                      placeholder="ex : 5000"
                      className="text-sm"
                      data-testid="devis-mpr-montant"
                    />
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">
                      Catégorie ANAH
                    </label>
                    <Select
                      value={mprCategorie || 'none'}
                      onValueChange={(v) => setMprCategorie(v === 'none' ? '' : v)}
                      disabled={!isEditing}
                    >
                      <SelectTrigger className="w-full" data-testid="devis-mpr-cat">
                        <SelectValue placeholder="Non défini" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">—</SelectItem>
                        <SelectItem value="bleu">Bleu (très modeste)</SelectItem>
                        <SelectItem value="jaune">Jaune (modeste)</SelectItem>
                        <SelectItem value="violet">Violet (intermédiaire)</SelectItem>
                        <SelectItem value="rose">Rose (supérieur)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* --- Onglet 2 : Analyse & Chantier --- */}
        <TabsContent value="suivi" className="space-y-6 mt-0">
          {/* Rentabilité Widget */}
          <RentabiliteWidget devisId={id} />

          {/* Notes */}
          {devis.notes && (
            <div className="bg-white rounded-lg p-6 shadow-sm border border-slate-200">
              <h3 className="font-semibold text-slate-900 mb-2">Notes</h3>
              <p className="text-slate-600">{devis.notes}</p>
            </div>
          )}
          
          {/* Photos Section */}
          <div className="bg-white rounded-lg p-6 shadow-sm border border-slate-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <Camera className="w-4 h-4 text-green-600" />
                Photos du chantier ({photos.length})
              </h3>
              <div>
                <input
                  type="file"
                  ref={photoInputRef}
                  onChange={handlePhotoUpload}
                  accept="image/*"
                  multiple
                  className="hidden"
                />
                <Button 
                  size="sm" 
                  onClick={() => photoInputRef.current?.click()}
                  disabled={uploadingPhotos}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {uploadingPhotos ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  Ajouter photos
                </Button>
              </div>
            </div>
            
            {photos.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {photos.map((photo, idx) => (
                  <div key={idx} className="relative group">
                    <img 
                      src={`${process.env.REACT_APP_BACKEND_URL}/api/storage/${photo}`}
                      alt={`Photo ${idx + 1}`}
                      className="w-full h-32 object-cover rounded-lg border border-slate-200"
                      onError={(e) => e.target.src = '/placeholder-image.png'}
                    />
                    <button
                      onClick={() => handleDeletePhoto(idx)}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500 border-2 border-dashed border-slate-200 rounded-lg">
                <Camera className="w-12 h-12 mx-auto mb-2 opacity-30" />
                <p>Aucune photo ajoutée</p>
                <p className="text-sm">Cliquez sur "Ajouter photos" pour uploader</p>
              </div>
            )}
          </div>
          
          {/* AI Analysis Report */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
            <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-200 flex justify-between items-center">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
                Rapport IA - Points Sensibles
              </h3>
              <Button 
                size="sm" 
                variant="outline"
                onClick={runAnalysis}
                disabled={analyzingDevis}
                className="border-amber-300 text-amber-700 hover:bg-amber-100"
              >
                {analyzingDevis ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                {analyzingDevis ? 'Analyse...' : 'Analyser'}
              </Button>
            </div>
            
            {analysis ? (
              <div className="p-4 space-y-4">
                {/* Score Global */}
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="font-medium">Score Global</span>
                  <div className="flex items-center gap-2">
                    <div className="w-32 h-3 bg-slate-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full ${analysis.score_global >= 70 ? 'bg-green-500' : analysis.score_global >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                        style={{ width: `${analysis.score_global}%` }}
                      />
                    </div>
                    <span className={`font-bold ${analysis.score_global >= 70 ? 'text-green-600' : analysis.score_global >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                      {analysis.score_global}/100
                    </span>
                  </div>
                </div>
                
                {/* Synthèse */}
                {analysis.synthese && (
                  <p className="text-slate-700 italic bg-blue-50 p-3 rounded-lg border-l-4 border-blue-400">
                    {analysis.synthese}
                  </p>
                )}
                
                {/* Alertes Critiques */}
                {analysis.alertes_critiques && analysis.alertes_critiques.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-semibold text-red-700 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      Alertes Critiques
                    </h4>
                    {analysis.alertes_critiques.map((alerte, idx) => (
                      <div key={idx} className="p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="font-medium text-red-800">{alerte.titre}</p>
                        <p className="text-sm text-red-700">{alerte.description}</p>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Prix Analyse */}
                {analysis.prix_analyse && (
                  <div className="space-y-2">
                    <h4 className="font-semibold text-slate-700 flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" />
                      Analyse des Prix
                      <Badge className={
                        analysis.prix_analyse.verdict === 'correct' ? 'bg-green-100 text-green-700' :
                        analysis.prix_analyse.verdict === 'trop_cher' ? 'bg-red-100 text-red-700' :
                        'bg-amber-100 text-amber-700'
                      }>
                        {analysis.prix_analyse.verdict === 'correct' ? 'Correct' :
                         analysis.prix_analyse.verdict === 'trop_cher' ? 'Trop élevé' : 'Sous-évalué'}
                      </Badge>
                    </h4>
                    {analysis.prix_analyse.details && (
                      <ul className="space-y-1 text-sm text-slate-600">
                        {analysis.prix_analyse.details.map((detail, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="text-slate-400">•</span>
                            {detail}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
                
                {/* Faisabilité */}
                {analysis.faisabilite && (
                  <div className="space-y-2">
                    <h4 className="font-semibold text-slate-700 flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Faisabilité
                      <Badge className={
                        analysis.faisabilite.niveau === 'simple' ? 'bg-green-100 text-green-700' :
                        analysis.faisabilite.niveau === 'moyen' ? 'bg-amber-100 text-amber-700' :
                        'bg-red-100 text-red-700'
                      }>
                        {analysis.faisabilite.niveau}
                      </Badge>
                    </h4>
                    {analysis.faisabilite.points && (
                      <ul className="space-y-1 text-sm text-slate-600">
                        {analysis.faisabilite.points.map((point, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="text-slate-400">•</span>
                            {point}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
                
                {/* Points de Vigilance */}
                {analysis.points_vigilance && analysis.points_vigilance.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-semibold text-amber-700 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Points de Vigilance
                    </h4>
                    <ul className="space-y-1 text-sm text-slate-600 bg-amber-50 p-3 rounded-lg">
                      {analysis.points_vigilance.map((point, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <span className="text-amber-500">⚠</span>
                          {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Recommandations */}
                {analysis.recommandations && analysis.recommandations.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-semibold text-green-700 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Recommandations
                    </h4>
                    <ul className="space-y-1 text-sm text-slate-600 bg-green-50 p-3 rounded-lg">
                      {analysis.recommandations.map((rec, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <span className="text-green-500">✓</span>
                          {rec}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Risques Juridiques */}
                {analysis.risques_juridiques && analysis.risques_juridiques.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-semibold text-purple-700">Risques Juridiques</h4>
                    <ul className="space-y-1 text-sm text-slate-600 bg-purple-50 p-3 rounded-lg">
                      {analysis.risques_juridiques.map((risque, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <span className="text-purple-500">§</span>
                          {risque}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <div className="p-8 text-center text-slate-500">
                <AlertTriangle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Aucune analyse disponible</p>
                <p className="text-sm">Cliquez sur "Analyser" pour générer un rapport IA</p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
      ) : (
        // Standard Devis Layout (non-AI)
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-lg p-6 shadow-sm border border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Informations Générales</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Statut</p>
                  <Badge className="mt-1" variant={getStatutBadgeVariant(devis.statut)}>{devis.statut}</Badge>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Type Prestation</p>
                  <Badge className="mt-1">{devis.type_prestation}</Badge>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Montant Estimé</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">
                    {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(devis.montant_estime)}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Date de création</p>
                  <p className="text-slate-900 mt-1">
                    {format(new Date(devis.date_creation), 'dd/MM/yyyy', { locale: fr })}
                  </p>
                </div>
              </div>
            </div>

            {devis.description && (
              <div className="bg-white rounded-lg p-6 shadow-sm border border-slate-200">
                <h2 className="text-lg font-semibold text-slate-900 mb-4">Description</h2>
                <p className="text-slate-700 whitespace-pre-wrap">{devis.description}</p>
              </div>
            )}
          </div>

          <div className="space-y-6">
            {contact && (
              <div className="bg-white rounded-lg p-6 shadow-sm border border-slate-200">
                <h3 className="font-semibold text-slate-900 mb-3">Contact</h3>
                <div className="space-y-2">
                  <p className="text-sm font-medium text-slate-900">{contact.nom_prenom}</p>
                  <p className="text-sm text-slate-600">{contact.telephone}</p>
                  {contact.email && <p className="text-sm text-slate-600">{contact.email}</p>}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full mt-3"
                    onClick={() => navigate(`/contacts/${contact.contact_id}`)}
                  >
                    Voir le contact
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Edit Dialog for non-AI devis */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className='max-w-2xl max-h-[90vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle>Modifier le Devis</DialogTitle>
          </DialogHeader>
          <DevisForm
            devis={devis}
            onSuccess={handleDevisUpdated}
            onCancel={() => setShowEdit(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Devis → Facture conversion dialog */}
      <ConvertDevisToFactureDialog
        open={showConvert}
        onOpenChange={setShowConvert}
        devis={devis}
      />
      
      {/* Status Update Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-slate-900 mb-6">Mettre à jour le statut</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Statut</label>
                <div className="grid grid-cols-3 gap-2">
                  {['En attente', 'Accepté', 'Refusé'].map((status) => (
                    <button
                      key={status}
                      onClick={() => setNewStatus(status)}
                      className={`p-3 rounded-lg border-2 font-medium transition-all ${
                        newStatus === status 
                          ? status === 'Accepté' ? 'border-green-500 bg-green-50 text-green-700' :
                            status === 'Refusé' ? 'border-red-500 bg-red-50 text-red-700' :
                            'border-amber-500 bg-amber-50 text-amber-700'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      {status}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Observation (optionnel)
                </label>
                <Textarea
                  value={statusObservation}
                  onChange={(e) => setStatusObservation(e.target.value)}
                  placeholder="Ex: Client a demandé un délai, Négociation en cours..."
                  rows={3}
                  data-testid="status-observation-input"
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-3 mt-6">
              <Button variant="outline" onClick={() => setShowStatusModal(false)}>
                Annuler
              </Button>
              <Button onClick={handleUpdateStatus} className="bg-green-600 hover:bg-green-700">
                Enregistrer
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Client Selector Modal */}
      {showClientSelector && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-900">Lier à un client</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowClientSelector(false)}>
                ✕
              </Button>
            </div>
            
            <div className="space-y-3">
              {gestionClients.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-slate-500 mb-4">Aucun client dans la gestion clients</p>
                  <Button onClick={() => navigate('/gestion-clients')}>
                    Ajouter un client
                  </Button>
                </div>
              ) : (
                <>
                  <p className="text-sm text-slate-500 mb-4">
                    Sélectionnez un client pour lier ce devis :
                  </p>
                  {gestionClients.map((client) => {
                    const cid = client.id || client.contact_id;
                    const isLinked = Boolean(cid && devis.gestion_client_id && devis.gestion_client_id === cid);
                    return (
                      <div
                        key={cid || client.nom}
                        onClick={() => !linkingClient && cid && handleLinkClient(cid)}
                        className={`p-4 rounded-lg border cursor-pointer transition-all ${
                          isLinked
                            ? 'border-green-500 bg-green-50'
                            : 'border-slate-200 hover:border-blue-300 hover:bg-blue-50'
                        } ${linkingClient ? 'opacity-50 pointer-events-none' : ''}`}
                        data-testid={`link-client-row-${cid || ''}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-slate-900">{client.nom}</p>
                            {client.contact_principal && (
                              <p className="text-sm text-slate-500">{client.contact_principal}</p>
                            )}
                            <div className="flex items-center gap-3 mt-1">
                              {client.ville && (
                                <span className="text-xs text-slate-400">{client.ville}</span>
                              )}
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                client.category === 'collectivites'
                                  ? 'bg-blue-100 text-blue-700'
                                  : 'bg-amber-100 text-amber-700'
                              }`}>
                                {client.category === 'collectivites' ? 'Collectivité' : 'Conducteur'}
                              </span>
                            </div>
                          </div>
                          {isLinked && (
                            <span key="linked-badge" className="text-green-600 font-medium">✓ Lié</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {devis.gestion_client_id && (
                    <div className="pt-4 border-t border-slate-200" data-testid="unlink-section">
                      <Button
                        variant="outline"
                        onClick={(e) => { e.stopPropagation(); handleUnlinkClient(); }}
                        disabled={linkingClient}
                        className="w-full border-red-200 text-red-700 hover:bg-red-50"
                        data-testid="unlink-client-btn"
                      >
                        Délier le client de ce devis
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
      <LastTouchedBadge entityType="devis" entityId={id} />

      {/* Modale Aperçu PDF (inline, sans téléchargement) */}
      <Dialog
        open={showPreview}
        onOpenChange={(o) => {
          if (!o) {
            setShowPreview(false);
            if (previewUrl) window.URL.revokeObjectURL(previewUrl);
            setPreviewUrl('');
          }
        }}
      >
        <DialogContent className="max-w-6xl w-[95vw] h-[92vh] p-0 flex flex-col" data-testid="devis-preview-modal">
          <DialogHeader className="px-4 py-2 border-b flex flex-row items-center justify-between shrink-0">
            <DialogTitle className="text-base">Aperçu — Devis {devis?.devis_id}</DialogTitle>
            <Button
              variant="outline" size="sm"
              onClick={handleOpenPdfInTab}
              className="ml-4"
              data-testid="devis-preview-open-tab"
            >
              Ouvrir dans un nouvel onglet
            </Button>
          </DialogHeader>
          <div className="flex-1 overflow-hidden bg-slate-100 relative">
            {previewUrl && (
              <object
                data={previewUrl}
                type="application/pdf"
                className="w-full h-full block"
                data-testid="devis-preview-iframe"
              >
                <div className="flex flex-col items-center justify-center h-full text-center p-6 gap-3">
                  <p className="text-sm text-slate-600">
                    Ton navigateur ne peut pas afficher le PDF ici.
                  </p>
                  <Button onClick={handleOpenPdfInTab} data-testid="devis-preview-fallback">
                    Ouvrir dans un nouvel onglet
                  </Button>
                </div>
              </object>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
